import React, { useState, useEffect, useRef } from 'react';

const LivePriceDocumentation = () => {
  // Get authentication data from localStorage
  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");

  // WebSocket states
  const [connected, setConnected] = useState(false);
  const [ws, setWs] = useState(null);
  const wsRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const connectionMonitorRef = useRef(null);
  const subscribedScripCodes = useRef(new Set());
  const lastSubscribedSecurities = useRef([]);

  // Live price caches - same as your implementation
  const livePriceCache = useRef({});
  const livePCloseCache = useRef({});
  const liveDiffCache = useRef({});
  const liveChangePercentageCache = useRef({});
  const liveVolumeCache = useRef({});
  const [liveUpdateTrigger, setLiveUpdateTrigger] = useState(0);

  // Sample securities for demonstration
  const [securities] = useState([
    { ScripCode: 999920006, ScripName: "Nifty 100", Exch: "N", ExchType: "C" },
    { ScripCode: 999920062, ScripName: "NIFTY200 QUALTY30", Exch: "N", ExchType: "C" },
    { ScripCode: 1922, ScripName: "INFY", Exch: "N", ExchType: "C" },
    { ScripCode: 4963, ScripName: "HDFC BANK", Exch: "N", ExchType: "C" },
    { ScripCode: 1594, ScripName: "ICICI BANK", Exch: "N", ExchType: "C" },
    { ScripCode: 2885, ScripName: "WIPRO", Exch: "N", ExchType: "C" },
    { ScripCode: 1348, ScripName: "SBI", Exch: "N", ExchType: "C" },
    { ScripCode: 1333, ScripName: "RELIANCE", Exch: "N", ExchType: "C" },
    { ScripCode: 1195, ScripName: "ITC", Exch: "N", ExchType: "C" },
    { ScripCode: 467, ScripName: "HDFCLIFE", Exch: "N", ExchType: "C" }
  ]);

  // Step 1: WebSocket Connection Handler
  const handleConnectClick = () => {
    console.log("🔵 STEP 1: Establishing WebSocket Connection");
    
    if (!sharedData?.accessToken || !sharedData?.ClientCode) {
      console.warn("❌ Cannot connect WebSocket — missing token or client code.");
      alert("Please login first to get access token and client code!");
      return;
    }

    const webSocket = new WebSocket(
      `wss://openfeed.5paisa.com/Feeds/api/chat?Value1=${sharedData.accessToken}|${sharedData.ClientCode}`
    );

    console.log("🔗 WebSocket URL:", webSocket.url);

    webSocket.onopen = () => {
      console.log("✅ STEP 1 COMPLETE: 5paisa WebSocket connected successfully");
      setConnected(true);
      setWs(webSocket);
      wsRef.current = webSocket;
      
      // Step 2: Setup message handler
      setupWebSocketMessageHandler(webSocket);
      
      // Step 3: Start heartbeat
      startHeartbeat();
      
      // Step 4: Subscribe to securities
      subscribeToSecurities();
    };

    webSocket.onerror = (error) => {
      console.error("❌ WebSocket error:", error);
      setConnected(false);
    };

    webSocket.onclose = (event) => {
      console.log(`🔴 WebSocket closed. Code: ${event.code}, Reason: ${event.reason}`);
      setConnected(false);
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  };

  // Step 2: Setup WebSocket Message Handler
  const setupWebSocketMessageHandler = (websocket) => {
    console.log("🔵 STEP 2: Setting up WebSocket Message Handler");
    
    const ws = websocket || wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.onmessage = (event) => {
        try {
          const receivedData = JSON.parse(event.data);
          
          // Handle heartbeat response
          if (receivedData.Method === "Heartbeat") {
            console.log("💗 Heartbeat response received");
            return;
          }
          
          // Handle live price data
          if (Array.isArray(receivedData) && receivedData[0]) {
            const data = receivedData[0];
            handleLivePriceUpdate(data);
          }
        } catch (error) {
          console.error("❌ Error processing WebSocket message:", error);
        }
      };
      console.log("✅ STEP 2 COMPLETE: Message handler setup complete");
    }
  };

  // Step 3: Start Heartbeat to Keep Connection Alive
  const startHeartbeat = () => {
    console.log("🔵 STEP 3: Starting Heartbeat Mechanism");
    
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({
            Method: "Heartbeat",
            Operation: "Ping",
            Timestamp: new Date().getTime()
          }));
          console.log("💗 Heartbeat sent");
        } catch (error) {
          console.error("❌ Error sending heartbeat:", error);
        }
      }
    }, 30000); // 30 seconds
    
    console.log("✅ STEP 3 COMPLETE: Heartbeat started (every 30 seconds)");
  };

  // Step 4: Subscribe to Securities for Live Price Updates
  const subscribeToSecurities = () => {
    console.log("🔵 STEP 4: Subscribing to Securities");
    
    const subscribeWhenReady = () => {
      lastSubscribedSecurities.current = securities;
      
      const checkConnection = () => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log("🚀 WebSocket ready, subscribing to securities...");
          securities.forEach((security) => {
            sendSubscriptionMessage(security);
          });
          console.log("✅ STEP 4 COMPLETE: All securities subscribed");
        } else {
          console.log("⏳ Waiting for WebSocket connection...");
          setTimeout(checkConnection, 500);
        }
      };
      
      setTimeout(checkConnection, 1000);
    };
    
    subscribeWhenReady();
  };

  // Step 5: Send Subscription Message for Each Security
  const sendSubscriptionMessage = (security) => {
    const currentWs = wsRef.current;
    
    if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket is not connected");
      return;
    }

    const priceMessage = {
      Method: "MarketFeedV3",
      Operation: "Subscribe",
      ClientCode: sharedData.ClientCode,
      MarketFeedData: [{
        Exch: security.Exch,
        ExchType: security.ExchType,
        ScripCode: security.ScripCode,
      }],
    };

    try {
      currentWs.send(JSON.stringify(priceMessage));
      console.log(`✅ STEP 5: Subscribed to ${security.ScripName} (${security.ScripCode})`);
      
      if (!subscribedScripCodes.current) {
        subscribedScripCodes.current = new Set();
      }
      subscribedScripCodes.current.add(security.ScripCode);
    } catch (error) {
      console.error("Error sending subscription message:", error);
    }
  };

  // Step 6: Handle Live Price Updates
  const handleLivePriceUpdate = (data) => {
    console.log("🔵 STEP 6: Processing Live Price Update");
    
    const { Token: scriptCode, LastRate: lastRateValue, PClose, Volume } = data;
    
    if (!scriptCode || lastRateValue === undefined) return;

    // Initialize caches if not exist
    if (!livePriceCache.current) livePriceCache.current = {};
    if (!livePCloseCache.current) livePCloseCache.current = {};
    if (!liveDiffCache.current) liveDiffCache.current = {};
    if (!liveChangePercentageCache.current) liveChangePercentageCache.current = {};
    if (!liveVolumeCache.current) liveVolumeCache.current = {};

    // Update price caches
    livePriceCache.current[scriptCode] = lastRateValue;
    livePCloseCache.current[scriptCode] = PClose;
    liveDiffCache.current[scriptCode] = lastRateValue - PClose;
    liveChangePercentageCache.current[scriptCode] = ((lastRateValue - PClose) / PClose) * 100;
    liveVolumeCache.current[scriptCode] = Volume;

    // Trigger UI update
    setLiveUpdateTrigger((prev) => prev + 1);
    
    console.log(`✅ STEP 6 COMPLETE: Price Update: ${scriptCode} = ₹${lastRateValue}`);
  };

  // Step 7: Connection Monitor for Auto-Reconnection
  const startConnectionMonitor = () => {
    console.log("🔵 STEP 7: Starting Connection Monitor");
    
    if (connectionMonitorRef.current) {
      clearInterval(connectionMonitorRef.current);
    }
    
    connectionMonitorRef.current = setInterval(() => {
      const currentWs = wsRef.current;
      
      if (!currentWs || currentWs.readyState === WebSocket.CLOSED || currentWs.readyState === WebSocket.CLOSING) {
        console.log("🔄 Connection lost, attempting to reconnect...");
        handleConnectClick();
        
        // Resubscribe after reconnection
        setTimeout(() => {
          if (lastSubscribedSecurities.current.length > 0) {
            console.log("🔄 Resubscribing after reconnection...");
            lastSubscribedSecurities.current.forEach((security) => {
              sendSubscriptionMessage(security);
            });
          }
        }, 1000);
      }
    }, 2000); // Check every 2 seconds
    
    console.log("✅ STEP 7 COMPLETE: Connection monitor started");
  };

  // Cleanup function
  const cleanupWebSocket = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (connectionMonitorRef.current) {
      clearInterval(connectionMonitorRef.current);
      connectionMonitorRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setConnected(false);
    setWs(null);
  };

  // Helper functions for formatting
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "-";
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPriceWithColor = (price, change) => {
    if (!price) return "-";
    const color = change > 0 ? '#00b894' : change < 0 ? '#e17055' : '#636e72';
    return <span style={{ color, fontWeight: '600' }}>₹{formatNumber(price)}</span>;
  };

  // UseEffect hooks
  useEffect(() => {
    startConnectionMonitor();
    
    return () => {
      cleanupWebSocket();
    };
  }, []);

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #e5e5e5' }}>
        <h1 style={{ color: '#333', margin: '0' }}>5paisa Live Price WebSocket Integration</h1>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
          
          {/* Connection Status */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '20px',
            fontWeight: '600',
            fontSize: '14px',
            background: connected ? '#e8f5e8' : '#fee',
            color: connected ? '#2d5a2d' : '#d63031'
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: connected ? '#00b894' : '#e17055',
              boxShadow: connected ? '0 0 8px #00b894' : '0 0 8px #e17055'
            }}></span>
            {connected ? 'Connected' : 'Disconnected'}
          </div>
          
          {/* Authentication Status */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '15px',
            fontWeight: '500',
            fontSize: '12px',
            background: sharedData ? '#e8f5e8' : '#fee',
            color: sharedData ? '#2d5a2d' : '#d63031'
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: sharedData ? '#00b894' : '#e17055'
            }}></span>
            {sharedData ? `Client: ${sharedData.ClientCode}` : 'Not Authenticated'}
          </div>
        </div>
      </div>

      {/* Steps Explanation */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#333', marginBottom: '20px' }}>How Live Price Integration Works</h2>
        
        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #0984e3' }}>
          <h3 style={{ color: '#2d3436', margin: '0 0 8px 0' }}>Step 1: WebSocket Connection</h3>
          <p style={{ color: '#636e72', margin: '0 0 8px 0' }}>Establish connection to 5paisa WebSocket server using access token and client code.</p>
          <code style={{ background: '#2d3436', color: '#a29bfe', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', wordBreak: 'break-all' }}>
            wss://openfeed.5paisa.com/Feeds/api/chat?Value1=ACCESS_TOKEN|CLIENT_CODE
          </code>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #0984e3' }}>
          <h3 style={{ color: '#2d3436', margin: '0 0 8px 0' }}>Step 2: Message Handler Setup</h3>
          <p style={{ color: '#636e72', margin: '0 0 8px 0' }}>Configure onmessage handler to process incoming live price data and heartbeat responses.</p>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #0984e3' }}>
          <h3 style={{ color: '#2d3436', margin: '0 0 8px 0' }}>Step 3: Heartbeat Mechanism</h3>
          <p style={{ color: '#636e72', margin: '0 0 8px 0' }}>Send periodic heartbeat messages every 30 seconds to keep connection alive.</p>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #0984e3' }}>
          <h3 style={{ color: '#2d3436', margin: '0 0 8px 0' }}>Step 4: Security Subscription</h3>
          <p style={{ color: '#636e72', margin: '0 0 8px 0' }}>Subscribe to MarketFeedV3 for each security using ScripCode, Exchange, and ExchangeType.</p>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #0984e3' }}>
          <h3 style={{ color: '#2d3436', margin: '0 0 8px 0' }}>Step 5: Live Data Processing</h3>
          <p style={{ color: '#636e72', margin: '0 0 8px 0' }}>Process incoming price updates and cache them for real-time display.</p>
        </div>

        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #0984e3' }}>
          <h3 style={{ color: '#2d3436', margin: '0 0 8px 0' }}>Step 6: Connection Monitoring</h3>
          <p style={{ color: '#636e72', margin: '0 0 8px 0' }}>Monitor connection status and automatically reconnect with resubscription if disconnected.</p>
        </div>
      </div>

      {/* Controls */}
      <div style={{ margin: '20px 0', textAlign: 'center' }}>
        <button 
          onClick={handleConnectClick} 
          disabled={connected}
          style={{
            padding: '12px 24px',
            background: connected ? '#a5a5a5' : '#0984e3',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: connected ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          {connected ? 'Connected' : 'Connect to 5paisa'}
        </button>
      </div>

      {/* Live Data Table */}
      <div style={{ marginTop: '30px' }}>
        <h2 style={{ color: '#333', marginBottom: '15px' }}>Live Price Data ({securities.length} Securities)</h2>
        
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <thead>
            <tr>
              <th style={{ background: '#2d3436', color: 'white', padding: '15px 10px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Security Name</th>
              <th style={{ background: '#2d3436', color: 'white', padding: '15px 10px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Scrip Code</th>
              <th style={{ background: '#2d3436', color: 'white', padding: '15px 10px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Current Price</th>
              <th style={{ background: '#2d3436', color: 'white', padding: '15px 10px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Previous Close</th>
              <th style={{ background: '#2d3436', color: 'white', padding: '15px 10px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Change</th>
              <th style={{ background: '#2d3436', color: 'white', padding: '15px 10px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Change %</th>
              <th style={{ background: '#2d3436', color: 'white', padding: '15px 10px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Volume</th>
            </tr>
          </thead>
          <tbody>
            {securities.map((security, index) => {
              const livePrice = livePriceCache.current[security.ScripCode];
              const pClose = livePCloseCache.current[security.ScripCode];
              const change = liveDiffCache.current[security.ScripCode];
              const changePercent = liveChangePercentageCache.current[security.ScripCode];
              const volume = liveVolumeCache.current[security.ScripCode];

              return (
                <tr key={security.ScripCode} style={{ background: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid #e5e5e5', fontSize: '14px', fontWeight: '600', color: '#2d3436' }}>
                    {security.ScripName}
                  </td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid #e5e5e5', fontSize: '14px' }}>
                    {security.ScripCode}
                  </td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid #e5e5e5', fontSize: '14px' }}>
                    {formatPriceWithColor(livePrice, change)}
                  </td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid #e5e5e5', fontSize: '14px' }}>
                    ₹{formatNumber(pClose)}
                  </td>
                  <td style={{ 
                    padding: '12px 10px', 
                    borderBottom: '1px solid #e5e5e5', 
                    fontSize: '14px',
                    color: change > 0 ? '#00b894' : change < 0 ? '#e17055' : '#636e72',
                    fontWeight: '600'
                  }}>
                    {change ? `₹${formatNumber(change)}` : '-'}
                  </td>
                  <td style={{ 
                    padding: '12px 10px', 
                    borderBottom: '1px solid #e5e5e5', 
                    fontSize: '14px',
                    color: changePercent > 0 ? '#00b894' : changePercent < 0 ? '#e17055' : '#636e72',
                    fontWeight: '600'
                  }}>
                    {changePercent ? `${formatNumber(changePercent)}%` : '-'}
                  </td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid #e5e5e5', fontSize: '14px' }}>
                    {volume ? formatNumber(volume) : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Code Explanation */}
      <div style={{ marginTop: '40px', padding: '20px', background: '#f1f2f6', borderRadius: '8px' }}>
        <h2 style={{ color: '#2d3436', marginBottom: '15px' }}>Key Implementation Points</h2>
        <ul style={{ listStyle: 'none', padding: '0' }}>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #ddd', color: '#636e72' }}>
            <strong style={{ color: '#2d3436' }}>Connection Management:</strong> WebSocket connection with automatic reconnection
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #ddd', color: '#636e72' }}>
            <strong style={{ color: '#2d3436' }}>Heartbeat System:</strong> Keeps connection alive with periodic ping messages
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #ddd', color: '#636e72' }}>
            <strong style={{ color: '#2d3436' }}>Subscription Management:</strong> Track subscribed securities to prevent duplicates
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #ddd', color: '#636e72' }}>
            <strong style={{ color: '#2d3436' }}>Data Caching:</strong> Use useRef for caches to persist data across re-renders
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #ddd', color: '#636e72' }}>
            <strong style={{ color: '#2d3436' }}>Error Handling:</strong> Robust error handling for connection and message parsing
          </li>
          <li style={{ padding: '8px 0', color: '#636e72' }}>
            <strong style={{ color: '#2d3436' }}>Cleanup:</strong> Proper cleanup of intervals and connections on unmount
          </li>
        </ul>
      </div>
    </div>
  );
};

export default LivePriceDocumentation;
